import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { AlertCircle } from "@untitledui/icons";
import { useMemo } from "react";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { AvatarLabelGroup } from "@/components/base/avatar/avatar-label-group";
import {
	DetailField,
	DetailFieldGrid,
	DetailFieldSection,
} from "@/components/base/detail-field/detail-field";
import { Label } from "@/components/base/label";
import { EmptyValue } from "@/components/common";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import { Illustration } from "@/components/shared-assets/illustrations";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { organizationQueryOptions } from "@/services/organization";

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/roles/(roles)/$roleId/details",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/roles/(roles)/$roleId/details",
	});

	// Decode the role name from the URL parameter
	const roleNameFromUrl = decodeURIComponent(params.roleId);

	// Fetch all global roles
	const {
		data: globalRoles = [],
		isLoading,
		isError: hasError,
	} = useQuery(organizationQueryOptions.globalRoles.list());

	// Find the specific role by name
	const role = useMemo(
		() => globalRoles.find((r) => r.name === roleNameFromUrl),
		[globalRoles, roleNameFromUrl],
	);

	const scope = role?.scope || "Organization";
	const isPlatformRole = scope === "Platform";

	if (isLoading)
		return (
			<div className="flex items-center justify-center text-center h-[calc(100vh-16rem)]">
				<LoadingIndicator type="line-simple" size="xl" supportingText />
			</div>
		);

	if (hasError)
		return (
			<div className="flex flex-col items-center justify-center h-full gap-4">
				<Illustration type="cloud" size="sm">
					<div className="bg-error-500 rounded-full p-3">
						<AlertCircle className="size-6" />
					</div>
				</Illustration>
				<div className="flex flex-col items-center justify-center pt-4">
					<p className="text-gray-900 font-semibold text-md">
						{t("admin.organizations.errors.errorLoadingPage")}
					</p>
					<div className="w-[60%]">
						<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
							{t("admin.organizations.errors.errorLoadingContent")}
						</p>
					</div>
				</div>
				{/* <div className="flex gap-3 justify-center">
					<Button
						color="primary"
						size="md"
						onClick={() => {
							// Reset the error boundary
							queryErrorResetBoundary.reset();
							// Invalidate the route to reload loaders
							router.invalidate();
						}}
					>
						{t("common.actions.retry")}
					</Button>
				</div> */}
			</div>
		);

	return (
		<div>
			<div className="flex items-center justify-between">
				<div className="py-4 px-3 min-w-60 md:min-w-50 lg:min-w-100 ">
					<AvatarLabelGroup
						size="md"
						title={t("admin.roles.drawer.title")}
						hideAvatar={true}
					/>
				</div>
			</div>

			<div className="bg-gray-50 rounded-lg p-4">
				<div className="space-y-4">
					<DetailFieldGrid>
						<DetailFieldSection>
							<DetailField
								label={t("OrganizationRole.Name", {
									useFieldInfo: true,
								}).toUpperCase()}
								value={role?.name ?? <EmptyValue />}
								valueClassName="font-bold text-gray-900"
							/>

							<DetailField
								label={t("admin.roles.scope")}
								value={
									<Label size="sm" type="default" showIcon={false}>
										{isPlatformRole
											? t("admin.roles.platform")
											: t("admin.roles.organization")}
									</Label>
								}
							/>
							<DetailField
								label={t("OrganizationRole.Id", {
									useFieldInfo: true,
								}).toUpperCase()}
								value={<CopyableId value={role?.id || ""} detailText />}
								variant="muted"
								valueClassName="text-md font-regular text-gray-500"
							/>
						</DetailFieldSection>

						<DetailFieldSection>
							<DetailField
								label={t("OrganizationRole.Description", {
									useFieldInfo: true,
								}).toUpperCase()}
								value={role?.description ?? <EmptyValue />}
								valueClassName="font-bold text-gray-900"
							/>
						</DetailFieldSection>
					</DetailFieldGrid>
				</div>
			</div>
		</div>
	);
}
